/**
 * Synchronous access to the signed-in account, in the current workspace.
 *
 * This module keeps its old name and its old shape deliberately: about twenty
 * components call `decodeClaims()` synchronously during render to read the
 * user's id, role, board and grade. What changed is where the answer comes from,
 * twice over.
 *
 * Originally: the access token was pulled out of localStorage and decoded in the
 * browser — unverified, and one refresh behind the database.
 *
 * Then: `AuthGate` fetched `GET /me` and published the server's answer here.
 *
 * Now: that answer is per-WORKSPACE. `role`, `board` and `grade` are properties
 * of a membership, not of a person, so the values below describe the caller in
 * the tenant this tab is in. Nothing here is trusted for authorization — every
 * endpoint re-derives the caller from the session and the resolved tenant.
 */
import type {
  AccountDTO,
  Board,
  Grade,
  LoginSurface,
  MembershipRole,
  PlatformRole,
} from '@cwy/config/contracts';
import { MEMBERSHIP_ROLE_LABEL, PLATFORM_ROLE_LABEL, gradeLabel } from '@cwy/config/contracts';

/** The shape the components already expect. `sub` is kept as the user id. */
export interface Claims {
  sub: string;
  /** The caller's role IN THIS WORKSPACE. Null when they have no membership. */
  role: MembershipRole | null;
  /** Authority over the platform itself. 'USER' for everyone normal. */
  platformRole: PlatformRole;
  email: string | null;
  emailVerified: boolean;
  name: string | null;
  board: Board | null;
  grade: Grade | null;
  /** Canonical subject names this teacher is assigned to. Empty for students. */
  subjects: string[];
  tenantSlug: string | null;
  tenantName: string | null;
  /** Blocks every screen but the password change until cleared. */
  mustChangePassword: boolean;
  /** True when this session was minted through the development bypass. */
  viaDevelopmentBypass: boolean;
  /** Every workspace this person can switch into. */
  memberships: AccountDTO['memberships'];
}

let cached: Claims | null = null;

/**
 * The one mapping from the server's account to the claims shape.
 *
 * Shared by the synchronous cache below and the reactive `useClaims()` hook, so
 * the two can never describe the same person differently.
 */
export function claimsFromAccount(account: AccountDTO | null): Claims | null {
  return account
    ? {
        sub: account.id,
        role: account.membership?.role ?? null,
        platformRole: account.platformRole,
        email: account.email,
        emailVerified: account.emailVerified,
        name: account.name,
        board: account.membership?.board ?? null,
        grade: account.membership?.grade ?? null,
        subjects: account.membership?.subjects ?? [],
        tenantSlug: account.tenant?.slug ?? null,
        tenantName: account.tenant?.name ?? null,
        mustChangePassword: account.mustChangePassword,
        viaDevelopmentBypass: account.viaDevelopmentBypass,
        memberships: account.memberships,
      }
    : null;
}

/** Publish the account. Called by AuthGate whenever `GET /me` resolves. */
export function setAccount(account: AccountDTO | null): void {
  cached = claimsFromAccount(account);
}

export function clearAccount(): void {
  cached = null;
}

/**
 * The current account, or null.
 *
 * Named `decodeClaims` for continuity with its call sites; it no longer decodes
 * anything.
 */
export function decodeClaims(): Claims | null {
  return cached;
}


/** "CBSE · Class 10", or an empty string when the scope is not set yet. */
export function boardGradeLabel(claims: Claims | null): string {
  if (!claims?.board || !claims.grade) return '';
  return `${claims.board} · Class ${gradeLabel(claims.grade)}`;
}

/**
 * How to describe the signed-in person in the app chrome.
 *
 * Platform authority outranks any membership: a super admin acting inside a
 * school should see "Super admin", because that is the authority their actions
 * carry and the thing they need to be conscious of.
 */
export function roleLabel(claims: Claims | null): string {
  if (!claims) return '';
  if (claims.platformRole !== 'USER') return PLATFORM_ROLE_LABEL[claims.platformRole];
  return claims.role ? MEMBERSHIP_ROLE_LABEL[claims.role] : 'No access';
}

/**
 * The workspace line shown under the role.
 *
 * Names the school for a school member, and stays empty in the first-party
 * tenant — a B2C student has no "workspace" worth naming, and labelling it would
 * imply an affiliation they do not have.
 */
export function workspaceLabel(claims: Claims | null): string {
  if (!claims?.tenantName) return '';
  return claims.tenantSlug === 'cwy' ? '' : claims.tenantName;
}

/**
 * The one surface this person belongs to.
 *
 * Every URL in the app sits under exactly one surface (see `surfaceForPath`), and
 * this is the other half of that pairing: where someone may go, and where they
 * land after signing in, are both derived from this single answer. Before it
 * existed the two were written out separately and disagreed — a school admin was
 * sent to /admin on sign-in by one copy and bounced to /teacher by another.
 *
 * Platform authority outranks any membership, because managing tenants is an
 * administrative job even for an operator who holds no membership at all.
 */
export function surfaceForRole(
  platformRole: PlatformRole,
  role: MembershipRole | null | undefined,
): LoginSurface {
  if (platformRole !== 'USER') return 'ADMIN';
  if (role === 'SCHOOL_ADMIN') return 'ADMIN';
  if (role === 'TEACHER' || role === 'COUNSELLOR') return 'TEACHER';
  return 'STUDENT';
}

/** The home page of each surface — where sign-in ends up. */
const SURFACE_HOME: Record<LoginSurface, string> = {
  STUDENT: '/today',
  TEACHER: '/teacher',
  ADMIN: '/admin',
};

export function surfaceHome(surface: LoginSurface): string {
  return SURFACE_HOME[surface];
}

/** Where a signed-in user should land, by role in the current workspace. */
export function landingPathFor(claims: Claims | null): string {
  if (!claims) return SURFACE_HOME.STUDENT;
  return SURFACE_HOME[surfaceForRole(claims.platformRole, claims.role)];
}

/** The same decision, from a raw account rather than the cache. */
export function landingPathForAccount(account: AccountDTO | null | undefined): string {
  if (!account) return SURFACE_HOME.STUDENT;
  return SURFACE_HOME[surfaceForRole(account.platformRole, account.membership?.role)];
}
